/**
 * Reports domain — entity types + non-hook helpers.
 *
 * Everything in this file is non-hook: the domain entity shapes (deal/chain/
 * door report rows + detail) and the read-hook response envelopes. Query
 * params and mutation-request types live in `hooks.ts` next to the hooks that
 * consume them (mirrors `services/products`).
 *
 * No blob/export helpers here (unlike `services/products`) — the reports
 * bulk-download endpoint returns an async 202 job (`SpecsheetBulkDownloadJob`)
 * handled entirely through the `useBulkDownloadDealReportSpecsheets` mutation
 * in `hooks.ts`, not an imperative blob fetch.
 */

// ── Domain entity types ─────────────────────────────────────────────

export type { DealReportList } from '../generated/types/DealReportList'
export type { DealReportDetail } from '../generated/types/DealReportDetail'
export type { ChainReportRow } from '../generated/types/ChainReportRow'
export type { DoorReportRow } from '../generated/types/DoorReportRow'
export type { ChainReportsSummary } from '../generated/types/ChainReportsSummary'
export type { DoorReportsSummary } from '../generated/types/DoorReportsSummary'
export type { ReportsDashboardOverviewResponse } from '../generated/types/ReportsDashboardOverviewResponse'
export type { ReportsSalesForecastResponse } from '../generated/types/ReportsSalesForecastResponse'
export type { ReportsDealsPipelineRow } from '../generated/types/ReportsDealsPipelineRow'
export type { ReportsMetricCard } from '../generated/types/ReportsMetricCard'
export type { ReportsForecastCard } from '../generated/types/ReportsForecastCard'
export type { ReportsForecastCharts } from '../generated/types/ReportsForecastCharts'

// ── Read-hook response envelopes ────────────────────────────────────
// (Query params + mutation-request types live in `hooks.ts`.)

export type { ListReportDealsQueryResponse } from '../generated/types/ListReportDeals'
export type { GetReportDealQueryResponse } from '../generated/types/GetReportDeal'
export type { ListChainReportsQueryResponse } from '../generated/types/ListChainReports'
export type { GetChainReportsSummaryQueryResponse } from '../generated/types/GetChainReportsSummary'
export type { ListDoorReportsQueryResponse } from '../generated/types/ListDoorReports'
export type { GetDoorReportsSummaryQueryResponse } from '../generated/types/GetDoorReportsSummary'
export type { GetReportsDashboardOverviewQueryResponse } from '../generated/types/GetReportsDashboardOverview'
export type { GetReportsSalesForecastQueryResponse } from '../generated/types/GetReportsSalesForecast'
export type { GetReportsDealsPipelineQueryResponse } from '../generated/types/GetReportsDealsPipeline'
export type { GetReportsSalesPipelineQueryResponse } from '../generated/types/GetReportsSalesPipeline'

// ── Non-hook helpers ─────────────────────────────────────────────────

import { bulkUpdateDealReports } from '../generated/clients/bulkUpdateDealReports'
import type { BulkAsyncActionResponse } from '../generated/types/BulkAsyncActionResponse'
import type { ReportsDealUpdateFieldsRequest } from '../generated/types/ReportsDealUpdateFieldsRequest'

/**
 * Adapts the raw `bulkUpdateDealReports` response body into the
 * `{status, data}` shape the bulk-edit modals (`BaseSelectModal`,
 * `AddDealsOverrideModal`, `DealsOverrideModal`) already branch on for the
 * legacy `api.bulkEditDeal` axios response
 * (`res.status === 202` + `res.data.message` for the async path). The
 * generated client only returns the response body (no HTTP status), so
 * `is_async` stands in for the 202-vs-200 distinction those call sites read.
 */
export function toBulkAsyncActionResult(result: BulkAsyncActionResponse) {
  return { status: result.is_async ? 202 : 200, data: result }
}

/**
 * Shared bulk-update call for the single-field select modals
 * (`AccountOwnerSelectModal`/`CampaignSelectModal`/`SalesStageSelectModal`):
 * splits the caller's `{params, include_ids?, exclude_ids?}` bag into the
 * fetcher's body (`include_ids`/`exclude_ids`) vs query-string config
 * (`params`, read by `DealReportsBulkUpdateView.filter_queryset` to scope a
 * filtered "select all"), then adapts the response via
 * `toBulkAsyncActionResult` so callers can reuse their existing
 * axios-response-shaped success handlers unchanged.
 */
export async function bulkUpdateDealReportsSingleField(
  deal: ReportsDealUpdateFieldsRequest,
  rest: {
    params?: Record<string, unknown>
    include_ids?: number[]
    exclude_ids?: number[]
  }
) {
  const { params: queryParams, ...body } = rest
  const result = await bulkUpdateDealReports(
    { deal, ...body },
    { params: queryParams }
  )
  return toBulkAsyncActionResult(result)
}
